import {
  type PropertyComparableResponse,
  type PropertyDetailsResponse,
  type PropertyEdits,
  type PropertySearchResponse,
  type AccessUser,
  Section,
  FeatureFlag,
  Column,
  SectionColumn,
  CancelablePromise
} from 'common'
import { parseISO, format } from 'date-fns'
import { colors } from 'quasar'
import auth0Plugin from '@/setups/authSetup'

export type ColumnData = Record<
  Column,
  {
    description: string
    label: string
  }
>

// FORMAT FUNCTIONS

export const formatQuality = (val: number | null | undefined, missing = ''): string => {
  return val !== null && val !== undefined ? `${(val * 10).toFixed(0)}/10` : missing
}

// TODO: use the one from common/utils.ts instead
export const formatPercent = (
  val: number | null,
  decimal = 1,
  plusSign = false,
  missing = ''
): string => {
  if (plusSign && val !== null && val >= 0) {
    return `+${(val * 100).toFixed(decimal)}%`
  }
  return val !== null ? `${(val * 100).toFixed(decimal)}%` : missing
}

// TODO: use the one from common/utils.ts instead
export const formatMoney = (val: number | null, decimals?: number): string => {
  if (decimals !== undefined) {
    return val !== null
      ? `$${val.toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        })}`
      : ''
  }
  return val !== null ? `$${val.toLocaleString('en-US')}` : ''
}

// TODO: use the one from common/utils.ts instead
export const formatNumber = (
  val: number | null,
  sign: boolean = false,
  digits: number = 0,
  thousandsSeparator: boolean = true
): string => {
  const options = {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: thousandsSeparator
  }
  if (sign === true && val !== null) {
    return val > 0
      ? `+${val.toLocaleString('en-US', options)}`
      : val.toLocaleString('en-US', options)
  }
  return val !== null ? val.toLocaleString('en-US', options) : ''
}

export const formatDate = (val: string | null): string => {
  return val ? format(parseISO(val), 'M/d/yyyy, h:mm a') : ''
}

export const formatKey = (label: string): string => {
  return label
    .replace(/^(avg_quality_score_|property_|quality)/g, '')
    .split('_')
    .map((_) => _.slice(0, 1).toUpperCase() + _.slice(1))
    .join(' ')
}

export const formatFloorplanName = (name: string | null, beds: number | null): string => {
  // formats floorplan name to fill
  if (name) {
    return name
  } else if (beds === 0) {
    return 'Studio'
  }
  return ''
}

// AGGREGATION FUNCTIONS

export const avg = (vals: (number | null)[]): number | null => {
  const filtered = vals.filter((val): val is number => val !== null)
  return filtered.length > 0 ? filtered.reduce((a, b) => a + b, 0) / filtered.length : null
}

export const min = (values: number[]): number | null => {
  return values.length ? Math.min(...values) : null
}

export const max = (values: number[]): number | null => {
  return values.length ? Math.max(...values) : null
}

export const sum = (vals: (number | null)[]): number => {
  const filtered = vals.filter((val): val is number => val !== null)
  return filtered.length > 0 ? filtered.reduce((a, b) => a + b, 0) : 0
}

export const percentile = (perc: number, vals: number[]): number => {
  const sorted = vals.filter((_) => _ !== null).sort((a, b) => a - b)
  const index = Math.floor((sorted.length - 1) * perc)
  return sorted[index]
}

/**
 * Allows one to apply multiple rolling functions to an array of values, for example avg, min, max, sum, percentile, etc.
 * Note: this is a centered rolling window, and we narrow the window on the edges.
 *
 * @param arr the array of values to apply the rolling function to
 * @param windowSize the size of the rolling window. Should be an odd number, since it's centered.
 * @param fn the function to apply to the rolling window
 * @returns
 */
export function rollingFunctions<T>(
  arr: number[][],
  windowSize: number,
  fn: (vals: number[]) => T,
  trailing: boolean = false
): T[] {
  const half = Math.floor(windowSize / 2)
  const results = Array<T>(arr.length)

  for (let dateIdx = 0; dateIdx < arr.length; dateIdx++) {
    // compute the rolling window for each function
    const localArray: number[] = []

    if (!trailing) {
      for (let j = dateIdx - half; j <= dateIdx + half; j++) {
        if (j >= 0 && j < arr.length) {
          localArray.push(...arr[j])
        }
      }
    } else {
      for (let j = Math.max(0, dateIdx - windowSize + 1); j <= dateIdx; j++) {
        if (j >= 0 && j < arr.length) {
          localArray.push(...arr[j])
        }
      }
    }
    results[dateIdx] = fn(localArray)
  }

  return results
}

/**
 * Returns a function that calculates the percentiles of an array of values.
 * @param percs the percentiles to calculate
 * @returns a function that calculates the percentiles of an array of values
 */
export const percentileFn = (percs: number[]): ((arr: (number | null)[]) => (number | null)[]) => {
  if (percs.length === 0) {
    return () => []
  }
  return (arr: (number | null)[]) => {
    const sorted = arr.filter((val): val is number => val !== null).sort((a, b) => a - b)
    const lastIndex = sorted.length - 1
    if (sorted.length < arr.length) {
      return percs.map(() => null)
    }
    return percs.map((perc) => sorted[Math.floor(lastIndex * perc)])
  }
}

/**
 * Returns the mean and standard deviation of an array of values.
 * @param arr the array of values to calculate the mean and standard deviation of
 * @param usePopulation whether to use the population standard deviation or the sample standard deviation
 * @returns the mean and standard deviation of the array
 */
export const normalDistribution = (
  arr: number[],
  usePopulation: boolean = false
): { mean: number | null; stddev: number | null } => {
  if (arr.length === 0) {
    return { mean: null, stddev: null }
  }
  const mean = arr.reduce((acc, val) => acc + val, 0) / arr.length

  const stddev = Math.sqrt(
    arr.reduce((acc, val) => acc.concat((val - mean) ** 2), []).reduce((acc, val) => acc + val, 0) /
      (arr.length - (usePopulation ? 0 : 1))
  )
  return { mean, stddev }
}

// MISC FUNCTIONS

/**
 * When processing a large number of items, we want to limit the number of concurrent promises
 * to avoid overloading the server. This function takes an array of items, a limit, and a function
 * that returns a promise. It will call the function on each item, and wait for all promises to resolve.
 **/
export async function chunkedMapPromiseAll<T, V>(
  items: Array<T>,
  limit: number,
  fn: (item: T) => Promise<V>
): Promise<V[]> {
  let results: V[] = []

  for (let start = 0; start < items.length; start += limit) {
    const end = start + limit > items.length ? items.length : start + limit
    const slicedResults = await Promise.all(items.slice(start, end).map(fn))
    results = [...results, ...slicedResults]
  }

  return results
}

// GOOGLE MAP

export const lightMapTheme = [
  {
    elementType: 'geometry',
    stylers: [
      {
        color: '#f5f5f5'
      }
    ]
  },
  {
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [
      {
        color: '#f5f5f5'
      }
    ]
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    featureType: 'landscape.man_made',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    featureType: 'poi.attraction',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.business',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.government',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.medical',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5'
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    featureType: 'poi.place_of_worship',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.school',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi.sports_complex',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#888888'
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [
      {
        color: '#dadada'
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  },
  {
    featureType: 'road.local',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#999999'
      }
    ]
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5'
      }
    ]
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#c9c9c9'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#555555'
      }
    ]
  }
]

export const darkMapTheme = [
  {
    elementType: 'geometry',
    stylers: [
      {
        color: '#222222' // Darker background color
      }
    ]
  },
  {
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#dddddd' // Lighter text color
      }
    ]
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [
      {
        color: '#222222' // Darker text stroke
      }
    ]
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'landscape.man_made',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi',
    stylers: [
      {
        visibility: 'off' // Keep points of interest off
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [
      {
        color: '#555555' // Darker road geometry color
      }
    ]
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#dddddd' // Lighter arterial road text color
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [
      {
        color: '#555555' // Darker highway geometry color
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#dddddd' // Lighter highway text color
      }
    ]
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#dddddd' // Lighter local road text color
      }
    ]
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry',
    stylers: [
      {
        color: '#555555' // Darker transit line geometry color
      }
    ]
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [
      {
        color: '#555555' // Darker transit station geometry color
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#555555' // Darker water geometry color
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#dddddd' // Lighter water text color
      }
    ]
  }
]

const colorCache: Record<number, Record<number, string>> = {
  0: {},
  1: {},
  2: {},
  3: {},
  4: {}
}

const baseColors = ['#2196f3', '#4caf50', '#ffc107', '#ff5722', '#f44336']

/**
 * Returns different colors depending on the number of bedrooms
 * @param bed The number of bedrooms
 * @returns
 */
export const unitTypeColor = (bed: number | undefined | null, alpha: number = 1): string => {
  if (bed === undefined || bed === null || bed < 0 || bed > 4) {
    return colors.getPaletteColor('grey')
  }

  // Round alpha to nearest 0.1 to use cached values
  const roundedAlpha = Math.round(alpha * 10) / 10

  if (colorCache[bed][roundedAlpha]) {
    return colorCache[bed][roundedAlpha]
  }
  const color = alpha === 1 ? baseColors[bed] : colors.changeAlpha(baseColors[bed], alpha)
  colorCache[bed][roundedAlpha] = color

  return color
}

export const similarityColor = (percentage: number | null): string => {
  if (percentage === null) {
    return 'grey-3'
  }
  if (percentage <= 0.1) {
    return 'grey-3'
  }
  if (percentage <= 0.2) {
    return 'lime-2'
  }
  if (percentage <= 0.3) {
    return 'lime-3'
  }
  if (percentage <= 0.4) {
    return 'light-green-3'
  }
  if (percentage <= 0.5) {
    return 'light-green-4'
  }
  if (percentage <= 0.6) {
    return 'light-green-5'
  }
  if (percentage <= 0.7) {
    return 'green-5'
  }
  if (percentage <= 0.8) {
    return 'green-6'
  }
  if (percentage <= 0.9) {
    return 'green-7'
  }
  return 'green-8'
}

export const propertyDetailToSearch = (
  property: PropertyDetailsResponse
): PropertySearchResponse => {
  return {
    id: property.id,
    lat: property.lat,
    lon: property.lon,
    building_name: property.building_name,
    building_name_alias: property.building_name_alias,
    street_address: property.street_address,
    street_address_alias: property.street_address_alias,
    city: property.city,
    state: property.state,
    zip_code: property.zip_code,
    number_units: property.number_units,
    year_built: property.year_built
  }
}

export const compResponseToSearch = (comp: PropertyComparableResponse): PropertySearchResponse => {
  return {
    id: comp.id,
    lat: comp.lat,
    lon: comp.lon,
    building_name: comp.building_name,
    building_name_alias: comp.building_name_alias,
    street_address: comp.street_address,
    street_address_alias: comp.street_address_alias,
    city: comp.city,
    state: comp.state,
    zip_code: comp.zip_code,
    number_units: comp.number_units,
    year_built: comp.year_built
  }
}

/**
 * Generate a list of property edits based on the manual changes made to the property details.
 */
export const computePropertyEdits = (
  changes: Record<string, Record<string, string | number>>
): PropertyEdits[] => {
  if (!changes) {
    return []
  }
  return Object.entries(changes).map(([propertyId, details]) => {
    return {
      report_id: -1,
      property_id: propertyId,
      user_id: '',
      ...details
    }
  })
}

export const deconstructPropertyEdits = (
  edits: PropertyEdits[]
): Record<string, Record<string, string | number>> => {
  const result: Record<string, Record<string, string | number>> = {}
  for (const edit of edits) {
    if (!result[edit.property_id]) {
      result[edit.property_id] = {}
    }
    for (const key in edit) {
      if (
        key !== 'property_id' &&
        key !== 'report_id' &&
        key !== 'user_id' &&
        edit[key as keyof PropertyEdits] !== null
      ) {
        result[edit.property_id][key] = edit[key as keyof PropertyEdits] as string | number
      }
    }
  }
  return result
}

export const userToName = (user?: AccessUser): string => {
  if (user) {
    if (user.id === auth0Plugin.user.value?.sub) {
      return 'me'
    }
    if (user.given_name && user.family_name) {
      return `${user.given_name} ${user.family_name}`
    }
    return user.email
  }
  return 'Unknown User'
}

export const useHider = <T, K extends keyof T>(obj: T, keysToHide: K[]): Omit<T, K> => {
  const res = { ...obj }

  keysToHide.forEach((key) => {
    delete res[key]
  })

  return res as Omit<T, K>
}

export type TabName =
  | 'overview'
  | 'property'
  | 'unit'
  | 'feesAndAmenities'
  | 'expenseBenchmarks'
  | 'revenueManagement'
  | 'debug'

export interface TabComponentColumn {
  label: string
  description: string
  name: Column
}

export interface TabComponent {
  label: string
  name: Section
  description: string
  featureFlag?: FeatureFlag
  columns?: TabComponentColumn[]
}

export interface Tab {
  label: string
  name: TabName
  featureFlag?: FeatureFlag
  components: TabComponent[]
}

export interface VisibleColumn extends TabComponentColumn {
  isVisible: boolean
}

export interface VisibleComponent extends TabComponent {
  isVisible: boolean
  columns: VisibleColumn[]
  showColumns: boolean
}

export interface TabWithVisibleComponents extends Tab {
  components: VisibleComponent[]
}

export const applicationTabs: Tab[] = [
  {
    label: 'Overview',
    name: 'overview',
    components: [
      {
        label: 'Rent Comps Grid',
        name: Section.PROPERTY_GRID,
        description: 'Side by side comparisons of comps with a plethora of columns to choose from.'
      },
      {
        label: 'Market Alerts Table',
        name: Section.MARKET_ALERTS,
        description:
          'Identify recent trend changes between the subject property and selected comparables.'
      }
    ]
  },
  {
    label: 'Rent Comps',
    name: 'property',
    components: [
      {
        label: 'Map',
        name: Section.COMP_MAP,
        description: 'See your comps displayed on a map.'
      },
      {
        label: 'Rent Comps Table',
        name: Section.BUILDING_INFORMATION,
        description:
          'Compare your performance to the most statistically similar properties in your market.',
        columns: [
          {
            label: 'Similarity',
            name: Column.SIMILARITY,
            description:
              "A percentage from 0 to 100, where 100 means it's the most similar to your property. The score is based on several factors, which you can see by clicking the score. We adjust the importance of each factor depending on the market."
          },
          {
            label: 'Leased % (Adv)',
            name: Column.ESTIMATED_OCCUPANCY,
            description:
              'The advertised leased percentage is the ratio of vacant units to total units. For stabilized assets, vacant units are defined as those available for move-in within the next 7 days. For lease-ups, vacant units are defined as those currently marketed, regardless of move-in date.'
          },
          {
            label: 'Exposure',
            name: Column.EXPOSURE_PERCENT,
            description:
              'Exposure represents the percentage of units in a building currently listed as available for move-in within the next 6 months. This includes units that are either already vacant or not being renewed, divided by the total number of units in the building.'
          },
          {
            label: 'Year Built',
            name: Column.YEAR_BUILT,
            description:
              'The year the building was built. If no data is available, we show a prediction based on facade images.'
          },
          {
            label: '# Units',
            name: Column.NUMBER_UNITS,
            description:
              'The total number of units in the building. If no data is available, we show a prediction based on property characteristics, location and units that have been listed.'
          },
          {
            label: 'Stories',
            name: Column.NUMBER_STORIES,
            description:
              'The number of stories in the building. We show a prediction based on facade images if no data is available.'
          }
        ]
      },
      {
        label: 'Unit Type Cards',
        name: Section.UNIT_TYPE_METRICS,
        description: 'For each unit type in your comps, see name data points.'
      },
      {
        label: 'Rent by Unit Type Table',
        name: Section.UNIT_TYPE_TABLE,
        description:
          'Analyze rent and availability trends among your market with unit-type level data.',
        columns: [
          {
            label: '# Leased',
            name: Column.NUMBER_LEASED,
            description:
              'The total number of listings that have been leased within the selected time period.'
          },
          {
            label: '# Active',
            name: Column.CURRENTLY_AVAILABLE,
            description:
              'The total number of listings currently advertised as available for that property.'
          },
          {
            label: 'Days on Mkt',
            name: Column.DAYS_ON_MARKET,
            description:
              'The average number of days the units have been on the market. If a unit is still on the market, we include the number of days since it was first listed.'
          },
          {
            label: 'Min SF',
            name: Column.MIN_SQFT,
            description: 'The average minimum square footage of the units.'
          },
          {
            label: 'Avg SF',
            name: Column.SQFT,
            description: 'The average square footage of the units.'
          },
          {
            label: 'Max SF',
            name: Column.MAX_SQFT,
            description: 'The average maximum square footage of the units.'
          },
          {
            label: 'Min Rent',
            name: Column.MIN_MARKET_RENT,
            description:
              'The average minimum asking rent uses the most recent known asking rent for the units.'
          },
          {
            label: 'Avg Rent',
            name: Column.MARKET_RENT,
            description:
              'The average asking rent uses the most recent known asking rent for the units.'
          },
          {
            label: 'Max Rent',
            name: Column.MAX_MARKET_RENT,
            description:
              'The average maximum asking rent uses the most recent known asking rent for the units.'
          },
          {
            label: 'Avg PSF',
            name: Column.RENT_PSF,
            description:
              'The average asking rent per square foot uses the most recent known asking rent divided by the square footage of the unit.'
          },
          {
            label: 'NER',
            name: Column.EFFECTIVE_RENT,
            description:
              'The average effective rent uses the most recent known effective rents for the units. Our AI extracts the dollar amounts from concession banners and amortizes them over the lease term.'
          },
          {
            label: 'NER PSF',
            name: Column.EFFECTIVE_RENT_PSF,
            description:
              'The average effective rent per square foot uses the most recent known effective rent divided by the square footage of the unit.'
          },
          {
            label: 'Concession %',
            name: Column.CONCESSION_PERCENT,
            description: 'The average concession amount as a percent of asking rent.'
          },
          {
            label: 'Year Built',
            name: Column.YEAR_BUILT,
            description:
              'The year the building was built. If no data is available, we show a prediction based on facade images.'
          },
          {
            label: 'Trend',
            name: Column.TREND,
            description: 'Effective Rent PSF or Asking Rent PSF trend over time.'
          }
        ]
      },
      {
        label: 'Estimated Rent Roll Table',
        name: Section.ESTIMATED_UNIT_MIX,
        description:
          'Analyze rents, availabilities and time on market by floorplan or type for your property and its comps.',
        columns: [
          {
            label: '# Listings',
            name: Column.NUMBER_LISTINGS,
            description: 'The number of unique units encountered of this type since June 17, 2023.'
          },
          {
            label: '# Units',
            name: Column.NUMBER_UNITS,
            description:
              "The estimated total units are calculated by multiplying the percentage of listings by the building's total number of units."
          },
          {
            label: 'Leased %',
            name: Column.ESTIMATED_OCCUPANCY,
            description:
              'The advertised leased percentage is the ratio of vacant units to total units. For stabilized assets, vacant units are defined as those available for move-in within the next 7 days. For lease-ups, vacant units are defined as those currently marketed, regardless of move-in date.'
          },
          {
            label: 'Exposure %',
            name: Column.EXPOSURE_PERCENT,
            description:
              'Exposure represents the percentage of units in a building currently listed as available for move-in within the next 6 months. This includes units that are either already vacant or not being renewed, divided by the total number of units in the building.'
          },
          {
            label: 'Renewal %',
            name: Column.RENEWAL_PERCENT,
            description:
              'Estimated percentage of leases that we have seen in the trailing 12 months.'
          },
          {
            label: 'Days on Mkt',
            name: Column.DAYS_ON_MARKET,
            description:
              'The number of days this unit has been on the market. If the unit is still on the market, we show the number of days since it was first listed.'
          },
          {
            label: '# Apps / Cancels',
            name: Column.NUMBER_APPS_TO_CANCELS,
            description:
              'The number of applications and cancellations or denials this unit experienced while on the market. If a unit was off the market for two or more days before returning, we consider it a cancellation.'
          },
          {
            label: 'Cancel %',
            name: Column.CANCEL_PERCENT,
            description:
              'The percent of applications that resulted in a cancellation. If a unit was off the market for two or more days before returning, we consider it a cancellation.'
          },
          {
            label: 'Leased',
            name: Column.LEASED_DATE,
            description:
              'The percent of applications that resulted in a cancellation. If a unit was off the market for two or more days before returning, we consider it a cancellation.'
          },
          {
            label: 'SF',
            name: Column.SQFT,
            description: 'The square footage of the unit.'
          },
          {
            label: 'Rent',
            name: Column.MARKET_RENT,
            description:
              'The last asking rent is the most recent known asking rent for this unit. When only a floorplan was found, a range of asking rents is shown instead.'
          },
          {
            label: 'PSF',
            name: Column.RENT_PSF,
            description:
              'The rent per sqft is the most recent known asking rent divided by the square footage of the unit.'
          },
          {
            label: 'Effective PSF',
            name: Column.EFFECTIVE_RENT_PSF,
            description:
              'The effective rent per sqft is the most recent known effective rent divided by the square footage of the unit.'
          },
          {
            label: 'NER',
            name: Column.EFFECTIVE_RENT,
            description:
              'The net effective rent is the most recent known effective rent for this unit. Our AI extracts the dollar amounts from concession banners and amortizes them over the lease term. If only a floorplan is available, a range of effective rents is displayed instead.'
          },
          {
            label: 'Tradeout',
            name: Column.TRADEOUT_PERCENT,
            description:
              'Lease tradeout shows the change in asking rents between the latest lease and the previous lease. Calculated as a trailing average over the last 6 months of leasing activity.'
          },
          {
            label: 'Trend',
            name: Column.TREND,
            description: 'Asking rent trend over time.'
          }
        ]
      },
      {
        label: 'Weekly Market Activity Chart',
        name: Section.VOLUME_OVER_TIME,
        description: 'Monitor market activity with detailed data on new and leased units.'
      },
      {
        label: 'Rent Comparison Chart',
        name: Section.CHART_COMPARE,
        description:
          'Quickly compare asking and effective rents by various metrics within your market.'
      },
      {
        label: 'Historical Rent Trends Chart',
        name: Section.RENT_TREND,
        description: 'Track historical min, max and average rent trends in your market.'
      },
      {
        label: 'Historical Exposure Chart',
        name: Section.EXPOSURE_CHART,
        description:
          'Analyze the percentage of units that are listed for rent for any given day or following 6 months.'
      },
      {
        label: 'Future Availability Chart',
        name: Section.FUTURE_AVAILABILITY,
        description:
          'Monitor the future supply of availabilities for units that have issued a notice to vacate.'
      }
    ]
  },
  {
    label: 'Unit-Level Insights',
    name: 'unit',
    components: [
      {
        label: 'Unit name Metrics',
        name: Section.UNIT_METRICS,
        description: "Compare name metrics about your subject's units against its comps."
      },
      {
        label: 'Unit-Level Insights Table',
        name: Section.UNIT_DETAILS,
        description: 'Analyze competitor pricing and concessions with unit-level detail.'
      },
      {
        label: 'Lease Terms',
        name: Section.LEASE_TERMS,
        description: 'Monitor the best lease term offered across all units.'
      },
      {
        label: 'Price Recommendations Table',
        name: Section.PRICE_RECOMMENDATION,
        description:
          'Maximize revenue with unit-specific rent recommendations based on recently signed leases.'
      },

      {
        label: 'Unit Prices Over Time Table',
        name: Section.UNIT_PRICES_OVER_TIME,
        description:
          'Analyze price trends and their correlation with new listings and lease closings.'
      }
    ]
  },
  {
    label: 'Fees & Amenities',
    name: 'feesAndAmenities',
    components: [
      {
        label: 'Fees Table',
        name: Section.FEES,
        description: 'Compare fees charged by your subject property to what the market is charging.'
      },
      {
        label: 'Concessions Table',
        name: Section.SPECIALS_CONCESSIONS,
        description:
          'Compare concessions offered by your subject property to what the market is offering.'
      },
      {
        label: 'Finishes Table',
        name: Section.FINISHES,
        description: 'Compare finish levels across your competition to improve market positioning.'
      },
      {
        label: 'Amenities Tables',
        name: Section.AMENITIES,
        description:
          'Compare building and unit amenities across your competition to improve market positioning.'
      }
    ]
  },
  {
    label: 'Financial Analysis',
    name: 'expenseBenchmarks',
    featureFlag: FeatureFlag.EXPENSE_BENCHMARK,
    components: [
      {
        label: 'Financial Analysis Table',
        name: Section.FINANCIAL_ANALYSIS,
        description:
          "Our estimate of the subject property's stabilized income and operating expenses over the next year, based on operating metrics from similar properties."
      }
    ]
  },
  {
    label: 'Revenue Management',
    name: 'revenueManagement',
    featureFlag: FeatureFlag.REVENUE_MANAGEMENT,
    components: [
      {
        label: 'Rent Recommendation Highlights',
        name: Section.RECOMMENDATION_HIGHLIGHTS,
        description: "See key data points about your subject's renting habits"
      },
      {
        label: 'Rent Recommendation',
        name: Section.UNIT_MIX_RECOMMENDATION,
        description:
          'Leverage market dynamics among your comps to recommend the best rents for your units.'
      },
      {
        label: 'Volume Over Time',
        name: Section.FORECASTING,
        description:
          'Various charts documenting Leasing Velocity, Average Price, Exposure, Time on Market, and Vacancy.'
      }
    ]
  }
]

export const getVisibleColumnsForSection = (
  visibleColumns: SectionColumn[],
  section: Section
): Column[] => {
  return visibleColumns.reduce((acc: string[], sectionColumn: string): Column[] => {
    const splitSectionColumn = sectionColumn.split('.')
    if (splitSectionColumn[0] === section) {
      acc.push(splitSectionColumn[1])
    }
    return acc as Column[]
  }, [])
}

export const getToggleableColumnsForSection = (section: Section): ColumnData => {
  const sectionData = applicationTabs
    .flatMap((tab) => tab.components)
    .find((_) => _.name === section)?.columns

  if (!sectionData) {
    throw new Error(`Section ${section} not found`)
  }

  return sectionData.reduce((acc: ColumnData, column) => {
    acc[column.name] = {
      description: column.description,
      label: column.label
    }
    return acc
  }, {} as ColumnData)
}

export const filterColumns = (
  columnName: Column,
  toggleableColumn: ColumnData,
  visibleColumns: Column[]
) => {
  if (columnName in toggleableColumn) {
    return visibleColumns.includes(columnName)
  }
  return true
}

export const sortColumns = (columnAName: Column, columnBName: Column, columnOrder: Column[]) => {
  const aIndex = columnOrder.indexOf(columnAName)
  const bIndex = columnOrder.indexOf(columnBName)

  if (aIndex === -1 || bIndex === -1) {
    console.error(`Column not found: ${columnAName} or ${columnBName}`)
    return 0
  }
  return aIndex - bIndex
}

export const hasConcessions = (
  property: PropertyDetailsResponse,
  dateRange: {
    from: Date
    to: Date
  }
) => {
  const fromStr = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : null
  const toStr = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : null
  return (property.concessions_history || []).some((concession) => {
    if (fromStr && toStr) {
      return !(concession.to_date < fromStr || concession.from_date > toStr)
    }
    return true
  })
}

// A Cancelable promise wrapper so that we can chain promises and
// keep the cancelable caracteristic

export class CancelablePromiseWrapper<T> {
  private readonly currentPromise: CancelablePromise<T>

  constructor(promise: CancelablePromise<T>) {
    this.currentPromise = promise
  }

  then<TResult1 = T, TResult2 = never>(
    onFulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | null,
    onRejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ): CancelablePromiseWrapper<TResult1 | TResult2> {
    const newPromise = new CancelablePromise<TResult1 | TResult2>((resolve, reject, onCancel) => {
      this.currentPromise
        .then(
          (value) => {
            if (onFulfilled) {
              resolve(onFulfilled(value))
            } else {
              resolve(value as any)
            }
          },
          (reason) => {
            if (onRejected) {
              resolve(onRejected(reason))
            } else {
              reject(reason)
            }
          }
        )
        .catch(reject)

      onCancel(() => {
        this.currentPromise.cancel()
      })
    })

    return new CancelablePromiseWrapper(newPromise)
  }

  catch<TResult = never>(
    onRejected?: ((reason: any) => TResult | PromiseLike<TResult>) | null
  ): CancelablePromiseWrapper<T | TResult> {
    return this.then(null, onRejected)
  }

  cancel(): void {
    this.currentPromise.cancel()
  }

  // If you need to get the underlying promise
  unwrap(): CancelablePromise<T> {
    return this.currentPromise
  }
}
